import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";
import type { Product } from "@/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FavoriteHeartIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useCart, useWishlist } from "@/store";
import { Price } from "./price";
import { notifyAddedToCart } from "@/lib/cart-toast";
import { productHref } from "@/lib/product-link";
import { resolveSizePick, sizeAvailability } from "@/lib/product-size";
import { orderQty } from "@/lib/min-order";

// «Собери образ» (complete-the-look) карточка (компонент 3891:109837): фото
// 173×229 r4 с голым сердцем, цена tangelo с «-N%», название 14/14, затем
// «Выбрать размер ⌄» (161×32 r10, обводка 1px #EAEAEB, 12/16 w600) и «В корзину»
// (161×32 r8, фон #989AA2 12 %, 12/16 w600). Контент под фото утоплен на 6px.
export function OutfitCard({
  product
}: {
  product: Product;
}) {
  const {
    t
  } = useTranslation();
  const navigate = useNavigate();
  const saved = useWishlist(s => s.ids.includes(product.id));
  const toggleProduct = useWishlist(s_0 => s_0.toggleProduct);
  const setProductLikedOnServer = useWishlist(s_1 => s_1.setProductLikedOnServer);
  const addToCart = useCart(s_2 => s_2.add);
  const cover = product.photos[0];
  const soldOut = product.stock <= 0;
  const sizes = product.sizes;
  const variants = product.variants ?? [];
  // Размер выбирается прямо в карточке, как в кадре: вариант (SKU) — по
  // размеру. На страницу товара ведём, только если у товара несколько
  // цветов — цвет здесь не выбрать.
  const colors = new Set(variants.map(v => v.color?.trim().toLowerCase() || v.id));
  const needsPage = colors.size > 1;
  const [size, setSize] = useState(sizes.length === 1 ? sizes[0] : "");
  const [sizeHint, setSizeHint] = useState(false);
  const [busy, setBusy] = useState(false);
  const needsSize = sizes.length > 1 && !size;
  const variant = size ? resolveSizePick(variants, size) : variants.length === 1 ? variants[0] : undefined;
  const sizeStock = sizeAvailability(variants);
  const add = async () => {
    if (soldOut) return;
    if (needsPage) {
      navigate(productHref(product.id));
      return;
    }
    // Без размера в корзину не кладём — красная «Выберите размер» под
    // кнопкой (как на странице товара, A-15).
    if (needsSize) {
      setSizeHint(true);
      return;
    }
    setBusy(true);
    try {
      await addToCart({
        productId: product.id,
        variantId: variant?.id,
        size: variant?.size ?? (size || sizes[0] || ""),
        color: variant?.color ?? product.color,
        qty: orderQty(product)
      });
      notifyAddedToCart(t, () => navigate("/cart"), {
        photo: product.photos[0],
        brand: product.brand,
        name: product.name
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setBusy(false);
    }
  };
  return <div className="group flex flex-col gap-2">
			<div className="relative aspect-[3/4] overflow-hidden rounded-[4px] bg-osmix-gray-100 transition-shadow duration-300 group-hover:shadow-[0_12px_32px_-10px_rgba(0,0,0,0.22)]">
				<Link to={productHref(product.id, variant?.id)} className="absolute inset-0 outline-none focus-visible:ring-2 focus-visible:ring-ds-focus">
					{cover ? <img src={cover} alt={product.name} loading="lazy" className={cn("size-full object-cover transition-transform duration-300 group-hover:scale-105", soldOut && "opacity-60")} /> : <div className="flex size-full items-center justify-center type-small text-osmix-icon">
							{product.name}
						</div>}
				</Link>
				<button type="button" aria-label={saved ? t("wishlist.remove") : t("wishlist.add")} aria-pressed={saved} onClick={() => {
        // В избранное — с выбранным размером, если он есть.
        const chosen = size || undefined;
        const liked = toggleProduct(product.id, chosen);
        void setProductLikedOnServer(product.id, liked, chosen);
      }} className="absolute right-2 top-2 flex size-8 items-center justify-center rounded-full outline-none transition-transform hover:scale-105 focus-visible:ring-2 focus-visible:ring-ds-focus">
					{/* «Избранное на фото» 32 (DS FavoriteHeartIcon). */}
					<FavoriteHeartIcon active={saved} />
				</button>
			</div>

			<div className="flex flex-col gap-2 px-1.5">
				<Price value={product.price} oldValue={product.oldPrice} pill="tangelo" />
				<Link to={productHref(product.id, variant?.id)} className="line-clamp-1 text-[14px] leading-[14px] tracking-[-0.42px] text-[#1F1F1F] transition-colors hover:text-osmix-tangelo">
					{product.brand || product.name}
				</Link>

				{sizes.length > 1 && !needsPage ? <Select value={size} onValueChange={next => {
        setSize(next);
        setSizeHint(false);
      }}>
						<SelectTrigger size="sm" className="w-full justify-center gap-1.5 border-ds-line-2 bg-transparent px-3 text-[12px] font-semibold leading-4 text-ds-primary data-[placeholder]:text-ds-primary [&_svg:not([class*='text-'])]:text-ds-primary">
							<SelectValue placeholder={t("product.pickSize", {
            defaultValue: "Выбрать размер"
          })} />
						</SelectTrigger>
						<SelectContent>
							{sizes.map(s_3 => <SelectItem key={s_3} value={s_3} disabled={sizeStock.get(s_3) === 0}>
									{s_3}
								</SelectItem>)}
						</SelectContent>
					</Select> : null}

				<Button variant="gray" size="sm" block onClick={add} disabled={soldOut || busy}>
					{soldOut ? t("product.outOfStock") : busy ? t("product.adding") : t("product.addToCart")}
				</Button>
				{sizeHint && needsSize && <p role="alert" className="text-[12px] leading-4 text-ds-error">
						{t("product.selectSize")}
					</p>}
			</div>
		</div>;
}